import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import {
  Clock, Users, Eye, X, RefreshCw, MessageSquareText, ArrowUpRight, AlertTriangle,
  ChevronLeft, ChevronRight, ListChecks, Archive, ArchiveRestore,
  Ban, StickyNote, RotateCcw, CalendarPlus, Pencil, Trash2, Scissors,
} from "lucide-react";
import EditEntryModal from "../../components/EditEntryModal.jsx";
import { toast } from "../../components/Toast.jsx";
import { useApiData, apiFetch } from "../../lib/api.js";
import GradientHeader from "../../components/GradientHeader.jsx";
import { Stranka, Modal, Dlazdice, EmptyState, Hlaska, AkceZnovu, KartaSeznam, PruhFiltru, SekcePruh, Segment, SkeletonRows, Stitek } from "../../components/ui.jsx";
import { TrackerBody, TimeTabsSwitch, ACTIVITY_COLORS, QX_DESC, PUVOD_META, fmtDayCz, fmtHours } from "../hr/TimeReport.jsx";
import TimeKalendar from "../hr/TimeKalendar.jsx";
import FondUpravaModal from "../../components/FondUpravaModal.jsx";
import { getRealUser } from "../../lib/auth.js";

// === OPERATIONS - Time tracking (owner) ===
// Velký přehled měření času back-office týmu: jeden člověk na řádek, kalendář celého
// měsíce (přepnutí na týden), listování zpět jen po měsících s daty (do budoucna ne).
// KPI, rozpad po činnostech/kvadrantech/původu, denní reporty, náhled rozhraní člověka
// a správa záznamů (archivace, vidí jen owner).

const ROLE_LABEL = { manager: "Manager", csm: "CSM", sales: "Sales", nutritionist: "Nutritionist", nutricni: "Nutriční", marketing: "Marketing" };
// Tón štítku podle původu měření (živě / zpětný start / vloženo zpětně / blok od ownera).
const PUVOD_TON = { live: "ok", zpetny_start: "pozor", rucni: "problem", owner: "znacka" };
const QX_COLORS = { Q1: "bg-rose-500", Q2: "bg-emerald-500", Q3: "bg-amber-400", Q4: "bg-slate-400" };
const DAY_NAMES = ["Po", "Út", "St", "Čt", "Pá", "So", "Ne"];
const MONTH_NAMES = ["Leden", "Únor", "Březen", "Duben", "Květen", "Červen", "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec"];

function addDaysIso(iso, n) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function mondayOf(iso) {
  const dw = new Date(iso + "T00:00:00").getDay();
  return addDaysIso(iso, -(dw === 0 ? 6 : dw - 1));
}
function todayIso() {
  const d = new Date(new Date().toLocaleString("en-US", { timeZone: "Europe/Prague" }));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Vodorovný rozpad hodin (činnosti / kvadranty / původ) jako stacked bar + legenda.
function HoursBar({ map, colorFor, title }) {
  const entries = Object.entries(map || {}).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);
  const total = entries.reduce((s, [, v]) => s + v, 0);
  if (!total) return null;
  return (
    <div>
      <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1">{title}</div>
      <div className="flex h-2.5 rounded-full overflow-hidden bg-slate-100">
        {entries.map(([k, v]) => (
          <div key={k} className={colorFor(k, true)} style={{ width: `${(v / total) * 100}%` }} title={`${k}: ${v} h`} />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
        {entries.map(([k, v]) => (
          <span key={k} className="inline-flex items-center gap-1 text-[11px] text-slate-600">
            <span className={`w-2 h-2 rounded-full ${colorFor(k, true)}`} />
            {k} <span className="font-semibold tabular-nums">{v} h</span>
          </span>
        ))}
      </div>
    </div>
  );
}

// Barvy: činnosti mapuju z pill barev (bg-*-100) na sytější bar (bg-*-400).
function activityBarColor(key) {
  const pill = ACTIVITY_COLORS[key] || "bg-slate-100";
  return pill.replace(/bg-(\w+)-100/, "bg-$1-400").replace(/bg-(\w+)-200/, "bg-$1-400").split(" ")[0];
}

// Jedna buňka kalendáře.
//
// 4.9.2026 (fáze 4a): buňka se kreslila dvakrát, jednou pro telefon a jednou
// pro počítač (páry sm:hidden / hidden sm:inline), a na telefonu z ní mizel
// odznak chybějícího reportu i hodiny volna. Teď se kreslí jednou. Znaky ✓ a ✗
// nahradil barevný proužek pod hodinami: zelený report odevzdaný, červený
// chybí, fialový zadal owner. Volno je 🏝️ (jediná schválená výjimka z pravidla
// „emoji nahradit lucide ikonami").
function DayCell({ d, bulletsRequired, onOpen, selected }) {
  const budouciBlok = d.future && d.hours > 0;
  const cls = budouciBlok
    ? "bg-brand-50 border-brand-200"
    : d.future
    ? "bg-white border-dashed border-slate-200"
    : d.volno ? "bg-sky-50 border-sky-200"
    : d.missing ? "bg-rose-50 border-rose-200"
    : d.hours > 0 || d.filled ? "bg-emerald-50 border-emerald-200"
    : "bg-slate-50 border-slate-100";
  // Proužek stavu reportu. Prázdný den proužek nemá.
  const prouzek = budouciBlok ? "bg-brand"
    : d.volno || d.future ? ""
    : d.filled ? "bg-emerald-500"
    : d.missing ? "bg-rose-500"
    : "";
  const klikaci = d.hours > 0 && typeof onOpen === "function";
  const popis = [
    fmtDayCz(d.datum),
    d.volno ? (d.hours > 0 ? `volno, práce během volna ${d.hours} h` : "volno") : d.hours > 0 ? `${d.hours} h` : "nic naměřeno",
    bulletsRequired && !d.volno && !d.future ? (d.filled ? "report odevzdaný" : d.missing ? "report chybí" : "") : "",
    budouciBlok ? "zadal owner" : "",
  ].filter(Boolean).join(", ");
  return (
    <div
      className={`flex min-h-[52px] flex-col rounded-xl border p-1 sm:min-h-[68px] sm:rounded-xl sm:p-2 ${cls} ${
        selected ? "ring-2 ring-brand" : d.today ? "ring-2 ring-brand-400" : ""
      } ${klikaci ? "cursor-pointer transition-shadow hover:shadow-md" : ""}`}
      onClick={klikaci ? () => onOpen(d.datum) : undefined}
      title={popis}
    >
      <div className={`text-[11px] leading-none ${d.today ? "font-bold text-brand-deep" : "text-slate-400"}`}>
        {Number(String(d.datum).slice(8, 10))}
      </div>
      {d.volno && d.hours > 0 ? (
        // FW-156: práce během volna se počítá do odpracovaných, v buňce jsou vidět hodiny i volno.
        <div className="mt-auto flex items-center gap-0.5 text-sm font-bold leading-tight tabular-nums text-sky-800 sm:text-base" aria-label={`práce během volna ${d.hours} h`}>
          <span className="text-xs leading-none sm:text-sm" aria-hidden>🏝️</span>{d.hours}
        </div>
      ) : d.volno ? (
        <div className="mt-auto text-base leading-none sm:text-lg" aria-label="volno">🏝️</div>
      ) : (
        <div className="mt-auto text-sm font-bold leading-tight tabular-nums text-slate-800 sm:text-base">
          {d.hours > 0 ? d.hours : "—"}
        </div>
      )}
      {prouzek && <div className={`mt-0.5 h-1 w-full rounded-full sm:h-1.5 ${prouzek}`} />}
    </div>
  );
}

/** Legenda pod mřížkou. Dřív to byla samostatná karta nad seznamem lidí. */
function LegendaDnu({ bulletsRequired }) {
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-slate-100 pt-2">
      {bulletsRequired && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500"><span className="h-1 w-5 rounded-full bg-emerald-500" /> report odevzdaný</span>}
      {bulletsRequired && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500"><span className="h-1 w-5 rounded-full bg-rose-500" /> report chybí</span>}
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500"><span className="h-1 w-5 rounded-full bg-brand" /> zadal owner</span>
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500">🏝️ volno</span>
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-sky-800">🏝️ 2,5 práce během volna</span>
      <span className="text-[11px] font-semibold text-slate-500">číslo je počet hodin</span>
    </div>
  );
}

// Avatar s fotkou z HR evidence, fallback na iniciály (stejný vzor jako HR Lidé).
function Avatar({ row }) {
  const [imgFailed, setImgFailed] = useState(false);
  const inner = (row.foto && !imgFailed) ? (
    <img src={row.foto} alt={row.name} onError={() => setImgFailed(true)}
      className="w-12 h-12 rounded-full object-cover border border-slate-200"
      style={{ objectPosition: "50% 25%" }} />
  ) : (
    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-400 to-brand-600 text-white font-bold flex items-center justify-center text-sm">
      {(row.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
    </div>
  );
  return (
    <div className="relative w-12 h-12 shrink-0">
      {inner}
      {row.live && (
        // Zelené kolečko jako "online" na Slacku — právě měří čas. Pulzuje.
        <span title={`Právě měří čas${row.liveOblast ? ", " + row.liveOblast : ""}`}
          className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-500 border-2 border-white">
          <span className="absolute inset-0 rounded-full bg-emerald-500 opacity-70 animate-ping" />
        </span>
      )}
    </div>
  );
}

function fmtLiveDur(sec) {
  sec = Math.max(0, Math.floor(sec));
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
  const p2 = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${p2(m)}:${p2(s)}` : `${m}:${p2(s)}`;
}

// Živý indikátor: tikající doba měření + na čem člověk pracuje (činnost/úkol).
function LiveBadge({ row }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);
  const elapsed = row.liveSince ? (now - row.liveSince * 1000) / 1000 : 0;
  const cinnost = row.liveOblast || "měří čas";
  const detail = row.liveUkol || row.livePopis || "";
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1 max-w-full">
      <span className="relative flex w-2 h-2 shrink-0">
        <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-500 opacity-70 animate-ping" />
        <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-500" />
      </span>
      <span className="tabular-nums shrink-0">{fmtLiveDur(elapsed)}</span>
      <span aria-hidden className="w-px h-3 bg-emerald-600/40 shrink-0" />
      <span className="shrink-0">{cinnost}</span>
      {detail ? <span aria-hidden className="w-px h-3 bg-emerald-600/40 shrink-0" /> : null}
      {detail ? <span className="truncate font-normal">{detail}</span> : null}
    </span>
  );
}

function PersonCard({ row, onViewAs, onChanged, onUpravitBlok }) {
  const missingCount = row.days.filter((d) => d.missing).length;
  const [openDay, setOpenDay] = useState(null); // rozkliknutý den → bloky měření od-do
  // Zneplatnění bloku měření (jen owner): rozepsaný blok + text důvodu + stav.
  const [invId, setInvId] = useState(null);   // id bloku, u kterého owner píše důvod
  const [invNote, setInvNote] = useState("");
  const [invBusy, setInvBusy] = useState(false);
  const [invErr, setInvErr] = useState("");
  const submitInvalid = async (id, invalid, note) => {
    setInvBusy(true); setInvErr("");
    try {
      const r = await apiFetch("/api/time-report", {
        method: "POST",
        body: JSON.stringify({ action: "invalidateEntry", id, invalid, note }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Akce selhala");
      setInvId(null); setInvNote("");
      onChanged && onChanged();
    } catch (e) { setInvErr(e.message); }
    finally { setInvBusy(false); }
  };
  // 3.9.2026: blok zadaný ownerem se nezneplatňuje, ale rovnou maže/upravuje —
  // a to přímo z rozkliknutého dne, ať to owner nemusí hledat ve Správě záznamů.
  const smazOwnerBlok = async (id) => {
    setInvBusy(true); setInvErr("");
    try {
      const r = await apiFetch("/api/time-report", {
        method: "POST",
        body: JSON.stringify({ action: "ownerBlokSmazat", id }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Smazání selhalo");
      onChanged && onChanged();
    } catch (e) { setInvErr(e.message); }
    finally { setInvBusy(false); }
  };
  const [showReports, setShowReports] = useState(false);
  const reportsRef = useRef(null);
  const toggleReports = () => {
    setShowReports((s2) => {
      const next = !s2;
      if (next) setTimeout(() => reportsRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 80);
      return next;
    });
  };
  const reportDays = row.days.filter((d) => d.bulletsText);
  // Zarovnání kalendáře: první den období může být uprostřed týdne (měsíc nezačíná pondělím).
  const pad = (new Date(row.days[0].datum + "T00:00:00").getDay() + 6) % 7;
  return (
    <div className="glass min-w-0 overflow-hidden !p-0">
      {/* 4.9.2026: hlavička je gradientová lišta jako u ostatních karet. */}
      <div data-nadpis-karty className="flex items-center gap-3 bg-gradient-to-r from-brand-950 via-brand-900 to-brand-700 px-3 py-3 sm:px-4">
        <Avatar row={row} />
        <div className="min-w-0 flex-1">
          <div className="truncate font-serif text-lg font-bold leading-tight text-white demo-sensitive">{row.name}</div>
          <div className="truncate text-[11px] text-white/70">{ROLE_LABEL[row.role] || row.role}</div>
        </div>
        <span className="shrink-0 rounded-full bg-white/15 px-2.5 py-1 text-sm font-bold tabular-nums text-white" title="Naměřeno za období">{row.totalHours} h</span>
        <button onClick={() => onViewAs(row)} aria-label="Otevřít rozhraní tohoto člověka" title="Otevřít rozhraní tohoto člověka (jen ke čtení)"
          className="btn-ikona btn-ikona-tmava"><ArrowUpRight className="h-5 w-5" /></button>
      </div>

      {row.live && (
        <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2 sm:px-4"><LiveBadge row={row} /></div>
      )}

      {/* Chybějící reporty: pruh na celou šířku. Dřív se na telefonu vůbec nezobrazil. */}
      {missingCount > 0 && (
        <div className="flex items-center gap-1.5 bg-rose-50 px-3 py-2 sm:px-4">
          <AlertTriangle className="h-4 w-4 shrink-0 text-rose-600" />
          <span className="text-xs font-bold text-rose-700">{missingCount}× chybí denní report</span>
        </div>
      )}

      <div className="space-y-4 p-3 sm:p-4">

      {/* Kalendář: Po-Ne sloupce, řádek = týden. Budoucí dny šedé, dnešek zvýrazněný. */}
      <div>
      <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
        {DAY_NAMES.map((n, i) => (
          <div key={n} className={`text-center text-[11px] sm:text-[11px] font-bold uppercase tracking-wide pb-0.5 ${i >= 5 ? "text-slate-300" : "text-slate-400"}`}>{n}</div>
        ))}
        {Array.from({ length: pad }).map((_, i) => <div key={`pad-${i}`} />)}
        {row.days.map((d) => <DayCell key={d.datum} d={d} bulletsRequired={row.bulletsRequired}
          onOpen={(dt) => setOpenDay((cur) => (cur === dt ? null : dt))} selected={openDay === d.datum} />)}
      </div>
      <LegendaDnu bulletsRequired={row.bulletsRequired} />
      </div>

      {/* Detail rozkliknutého dne — bloky měření od-do (3.8.2026). */}
      {openDay && (() => {
        const den = row.days.find((x) => x.datum === openDay);
        const bloky = den?.entries || [];
        return (
          <div className="rounded-xl border border-brand/20 bg-brand-50/50 p-3">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-4 w-4 text-brand" />
              <span className="text-sm font-bold text-slate-800">Bloky měření {fmtDayCz(openDay)}</span>
              <span className="text-xs text-slate-400 tabular-nums">{den?.hours || 0} h celkem</span>
              <button type="button" onClick={() => setOpenDay(null)} className="btn-ikona ml-auto" title="Zavřít" aria-label="Zavřít"><X className="h-5 w-5" /></button>
            </div>
            {invErr && invId == null && <div className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-2 py-1 mb-2">{invErr}</div>}
            {bloky.length === 0 ? (
              <div className="text-xs text-slate-400 italic">Detail bloků pro tento den není k dispozici.</div>
            ) : (
              <div className="space-y-1">
                {bloky.map((b, i) => {
                  const pv = PUVOD_META[b.puvod] || PUVOD_META.live;
                  const editing = b.id != null && invId === b.id;
                  return (
                    <div key={b.id ?? i} className={`rounded-xl border ${b.invalid ? "border-rose-200 bg-rose-50/50" : "border-slate-200 bg-white"}`}>
                      <div className="flex items-center gap-2 px-2.5 py-1.5 flex-wrap">
                        <span className={`text-sm font-bold tabular-nums shrink-0 ${b.invalid ? "text-slate-400 line-through" : "text-slate-800"}`}>{b.od || "?"}–{b.do || "?"}</span>
                        <span aria-hidden className="w-px h-3 bg-slate-300 shrink-0" />
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${b.invalid ? "bg-slate-100 text-slate-400 line-through" : (ACTIVITY_COLORS[b.oblast] || "bg-slate-100 text-slate-700")}`}>{b.oblast}</span>
                        {b.qx && <span className="text-[11px] font-bold text-slate-500 bg-slate-100 rounded px-1.5 py-0.5 shrink-0" title={QX_DESC[b.qx]}>{b.qx}</span>}
                        <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded border shrink-0 ${pv.cls}`}>{pv.label}</span>
                        {(b.ukol_nazev || b.popis) && (
                          <>
                            <span aria-hidden className="w-px h-3 bg-slate-300 shrink-0" />
                            <span className={`text-xs flex-1 truncate min-w-[60px] ${b.invalid ? "text-slate-400" : "text-slate-600"}`}>{b.ukol_nazev || b.popis}</span>
                          </>
                        )}
                        {!(b.ukol_nazev || b.popis) && <span className="flex-1" />}
                        {b.invalid && <span className="text-[11px] font-bold text-rose-700 bg-rose-100 border border-rose-200 rounded px-1.5 py-0.5 shrink-0 inline-flex items-center gap-1"><Ban className="w-3 h-3" /> Zneplatněno</span>}
                        <span className={`text-xs font-semibold tabular-nums shrink-0 ${b.invalid ? "text-slate-400 line-through" : "text-slate-700"}`}>{fmtHours(b.sec)}</span>
                        {b.id != null && (b.invalid ? (
                          <button onClick={() => submitInvalid(b.id, false)} disabled={invBusy}
                            title="Vrátit platnost (blok se znovu započítá)"
                            className="btn-ikona"><RotateCcw className="h-5 w-5" /></button>
                        ) : b.puvod === "owner" ? (
                          <>
                            {typeof onUpravitBlok === "function" && (
                              <button onClick={() => onUpravitBlok({ ...b, datum: openDay, email: row.email })} disabled={invBusy}
                                title="Upravit tento blok (zadal jsi ho ty)"
                                className="btn-ikona"><Pencil className="h-5 w-5" /></button>
                            )}
                            <button onClick={() => smazOwnerBlok(b.id)} disabled={invBusy}
                              title="Smazat tento blok (zadal jsi ho ty)"
                              className="btn-ikona"><Trash2 className="h-5 w-5" /></button>
                          </>
                        ) : (
                          <button onClick={() => { setInvId(editing ? null : b.id); setInvNote(""); setInvErr(""); }} disabled={invBusy}
                            title="Zneplatnit tento blok měření (napsat důvod)"
                            className="btn-ikona"><Ban className="h-5 w-5" /></button>
                        ))}
                      </div>
                      {b.invalid && b.invalid_note && (
                        <div className="px-2.5 pb-2 -mt-0.5">
                          <div className="text-[11px] text-rose-800 bg-rose-50 border border-rose-100 rounded-lg px-2 py-1 inline-flex items-start gap-1.5 max-w-full">
                            <StickyNote className="w-3 h-3 mt-0.5 shrink-0" />
                            <span className="break-words">{b.invalid_note}{(b.invalid_by || b.invalid_at) && <span className="text-rose-400"> ({b.invalid_by}{b.invalid_at ? `, ${fmtDayCz(b.invalid_at)}` : ""})</span>}</span>
                          </div>
                        </div>
                      )}
                      {editing && (
                        <div className="px-2.5 pb-2 flex items-center gap-2 flex-wrap">
                          <input autoFocus value={invNote} onChange={(e) => setInvNote(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter" && invNote.trim()) submitInvalid(b.id, true, invNote.trim()); if (e.key === "Escape") setInvId(null); }}
                            placeholder="Důvod zneplatnění (povinné)…"
                            className="field min-w-[160px] flex-1" />
                          <button onClick={() => invNote.trim() && submitInvalid(b.id, true, invNote.trim())} disabled={invBusy || !invNote.trim()}
                            className="btn btn-danger btn-sm shrink-0">Zneplatnit</button>
                          <button onClick={() => setInvId(null)} disabled={invBusy}
                            className="btn btn-ghost btn-sm shrink-0">Zrušit</button>
                        </div>
                      )}
                      {editing && invErr && <div className="px-2.5 pb-2 text-[11px] text-rose-600">{invErr}</div>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* Rozpady vedle sebe, ať je karta přehledná i přes celou šířku */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-3 pt-1">
        <HoursBar map={row.byActivity} title="Činnosti" colorFor={(k) => activityBarColor(k)} />
        <div className="space-y-3">
          {Object.keys(row.byQuadrant || {}).length > 0 && (
            <HoursBar map={row.byQuadrant} title="Kvadranty (7 návyků)" colorFor={(k) => QX_COLORS[k] || "bg-slate-300"} />
          )}
          <HoursBar map={row.origins} title="Původ záznamů"
            colorFor={(k) => ({ live: "bg-emerald-400", zpetny_start: "bg-amber-400", rucni: "bg-rose-400" }[k] || "bg-slate-300")} />
        </div>
        {Object.keys(row.byUkol || {}).length > 0 ? (
          <div>
            <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1 inline-flex items-center gap-1"><ListChecks className="w-3 h-3" /> Úkoly</div>
            <div className="space-y-0.5">
              {Object.entries(row.byUkol).sort((a, b) => b[1] - a[1]).map(([nazev, h]) => (
                <div key={nazev} className="flex items-center gap-2 text-xs">
                  <span className="text-slate-700 truncate flex-1">{nazev}</span>
                  <span className="font-bold tabular-nums text-slate-900 shrink-0">{h} h</span>
                </div>
              ))}
            </div>
          </div>
        ) : <div />}
      </div>

      {/* Denní reporty (odrážky) — owner si projde, čemu se člověk věnoval */}
      {row.bulletsRequired && (
        <div ref={reportsRef} className="-mx-3 scroll-mt-20 border-t border-slate-100 sm:-mx-4">
          <SekcePruh
            title="Denní reporty"
            ikona={MessageSquareText}
            pocet={reportDays.length}
            otevreno={showReports}
            onToggle={toggleReports}
          />
          {showReports && (
            reportDays.length === 0
              ? <div className="px-3 pt-2 text-xs text-slate-400 sm:px-4">V tomto období žádný report.</div>
              : (
                <div className="grid grid-cols-1 gap-2 px-3 pt-2 sm:px-4 md:grid-cols-2">
                  {reportDays.map((d) => (
                    <div key={d.datum} className="bg-slate-50 rounded-xl p-2.5">
                      <div className="text-[11px] font-bold text-slate-500 mb-1">{fmtDayCz(d.datum)}</div>
                      <pre className="text-xs text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">{d.bulletsText}</pre>
                    </div>
                  ))}
                </div>
              )
          )}
        </div>
      )}
      </div>
    </div>
  );
}

// Správa záznamů (jen owner): archivace jednotlivých měření + archiv s obnovením.
// Archivovaný záznam zmizí ze všech součtů a přehledů, vidí ho jen owner tady.
function EntriesManager({ onChanged, onUpravitBlok }) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const [tab, setTab] = useState("active"); // active | archive
  const [entries, setEntries] = useState(null);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("");
  // 19.10.2026 (hlášení #172): owner zkrátí cizí zapomenuté měření (akce ownerZkratit).
  const [zkratit, setZkratit] = useState(null);

  const load = useCallback(async (which = tab) => {
    setErr("");
    try {
      const r = await apiFetch(`/api/time-report?${which === "archive" ? "archive=1" : "entries=1"}`);
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Načtení selhalo");
      setEntries(j.entries || []);
    } catch (e) { setErr(e.message); setEntries([]); }
  }, [tab]);

  const openAndLoad = () => {
    setOpen((o) => {
      const next = !o;
      if (next) {
        load("active");
        // UX: po rozbalení doskrolovat k obsahu, ať je vidět, co se otevřelo.
        setTimeout(() => boxRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
      }
      return next;
    });
  };
  const switchTab = (t) => { setTab(t); setEntries(null); load(t); };

  const act = async (action, id) => {
    setErr("");
    try {
      const r = await apiFetch("/api/time-report", { method: "POST", body: JSON.stringify({ action, id }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Akce selhala");
      await load(tab);
      onChanged();
    } catch (e) { setErr(e.message); }
  };

  const visible = (entries || []).filter((e) => {
    const q = filter.trim().toLowerCase();
    return !q || `${e.name} ${e.oblast} ${e.popis} ${e.ukol_nazev}`.toLowerCase().includes(q);
  });

  // 4.9.2026 (fáze 4a): sbalitelná karta ze vzorníku, segmentový přepínač a
  // řádky se štítky. Chování (načítání, archivace, mazání bloků) beze změny.
  return (
    <div ref={boxRef} className="scroll-mt-20">
      <KartaSeznam
        ikona={Archive}
        title="Správa záznamů"
        podtitulek="archivace jednotlivých měření (vidíš jen ty)"
        sbalitelne
        otevreno={open}
        onToggle={openAndLoad}
      >
        <div className="space-y-3 p-3 sm:p-4">
          <Segment
            aria-label="Záznamy nebo archiv"
            value={tab}
            onChange={switchTab}
            items={[{ key: "active", label: "Záznamy (14 dní)" }, { key: "archive", label: "Archiv" }]}
          />
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filtrovat…" className="field" />
          {err && <Hlaska ton="problem" title="Nepovedlo se">{err}</Hlaska>}
          {entries === null ? (
            <SkeletonRows rows={4} />
          ) : visible.length === 0 ? (
            <EmptyState
              icon={Archive}
              title={tab === "archive" ? "Archiv je prázdný" : "Žádné záznamy za posledních 14 dní"}
              hint={tab === "archive" ? "Archivované měření se sem přesune a jde vrátit zpět." : undefined}
            />
          ) : (
            <div className="max-h-96 divide-y divide-slate-100 overflow-y-auto overscroll-contain">
              {visible.map((e) => {
                const pv = PUVOD_META[e.puvod] || PUVOD_META.live;
                const popis = e.ukol_nazev || e.popis || "";
                return (
                  <div key={e.id} className="flex items-start gap-3 py-2.5">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        <span className="truncate text-sm font-semibold text-slate-800 demo-sensitive">{e.name}</span>
                        <span className="shrink-0 text-sm font-bold tabular-nums text-slate-800">{fmtHours(e.duration_sec)}</span>
                      </div>
                      {popis && <div className="mt-0.5 truncate text-xs text-slate-500">{popis}</div>}
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        <Stitek velikost="mala" ton="neutralni">{fmtDayCz(e.datum)} {e.od || "?"}{e.do ? `–${e.do}` : ""}</Stitek>
                        <Stitek velikost="mala" ton="znacka">{e.oblast}</Stitek>
                        <Stitek velikost="mala" ton={PUVOD_TON[e.puvod] || "neutralni"}>{pv.label}</Stitek>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      {/* 3.9.2026: blok zadaný ownerem jde kdykoliv upravit i smazat.
                          Vlastní měření se dál jen archivuje, aby zůstala stopa. */}
                      {tab === "active" && e.puvod !== "owner" && (e.duration_sec || 0) >= 120 && (
                        <button type="button" onClick={() => setZkratit({ ...e, jmeno: e.name, datumLabel: fmtDayCz(e.datum) })} title="Zkrátit měření" aria-label="Zkrátit měření" className="btn-ikona">
                          <Scissors className="h-5 w-5" />
                        </button>
                      )}
                      {tab === "active" && e.puvod === "owner" && typeof onUpravitBlok === "function" && (
                        <button type="button" onClick={() => onUpravitBlok(e)} title="Upravit blok" aria-label="Upravit blok" className="btn-ikona">
                          <Pencil className="h-5 w-5" />
                        </button>
                      )}
                      {tab === "active" ? (
                        <button
                          type="button"
                          onClick={() => act(e.puvod === "owner" ? "ownerBlokSmazat" : "archiveEntry", e.id)}
                          title={e.puvod === "owner" ? "Smazat blok" : "Archivovat (zmizí ze všech výpočtů, jde vrátit)"}
                          aria-label={e.puvod === "owner" ? "Smazat blok" : "Archivovat"}
                          className="btn-ikona"
                        >
                          {e.puvod === "owner" ? <Trash2 className="h-5 w-5" /> : <Archive className="h-5 w-5" />}
                        </button>
                      ) : (
                        <button type="button" onClick={() => act("restoreEntry", e.id)} title="Vrátit z archivu" aria-label="Vrátit z archivu" className="btn-ikona">
                          <ArchiveRestore className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </KartaSeznam>
      <EditEntryModal
        open={!!zkratit}
        entry={zkratit}
        ownerMode
        onSave={async (payload) => {
          setErr("");
          try {
            const r = await apiFetch("/api/time-report", { method: "POST", body: JSON.stringify(payload) });
            const j = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(j.error || "Zkrácení selhalo");
            await load(tab);
            onChanged();
            return true;
          } catch (e) { setErr(e.message); toast(e.message, "error"); return false; }
        }}
        onClose={() => setZkratit(null)}
      />
    </div>
  );
}

// Read-only náhled do rozhraní konkrétního člověka. Ukazuje stejné tři záložky,
// jaké má člověk ve svém vlastním Time reportu (Dnes | Kalendář | Statistika),
// jen s jeho daty: Dnes přes ?as=email, Kalendář i Statistika přes
// ?kalendar=1&as=email (obojí smí na backendu jen owner). Žádná akce se
// neodesílá — TrackerBody i TimeKalendar dostávají readOnly.
function ViewAsPanel({ person, onClose }) {
  const [tab, setTab] = useState("dnes");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(async () => {
    setErr("");
    try {
      const r = await apiFetch(`/api/time-report?as=${encodeURIComponent(person.email)}`);
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Načtení selhalo");
      setData(j);
    } catch (e) { setErr(e.message); }
  }, [person.email]);
  useEffect(() => { load(); }, [load]);
  // Obnovit má obnovit i kalendář/statistiku — ty mají vlastní fetch a poslouchají
  // globální event fw40-time-changed (stejně jako ve vlastním Time reportu).
  const refreshAll = useCallback(() => {
    load();
    window.dispatchEvent(new Event("fw40-time-changed"));
  }, [load]);
  // 1.11.2026 (hlášení #189): owner může člověku upravit měsíční fond. Není to
  // zápis za něj (měření ani report se nemění), ale ownerovo rozhodnutí nad fondem.
  const jeOwner = String(getRealUser()?.role || "") === "owner";
  const maFond = ["manager", "csm"].includes(String(person.role || "").toLowerCase());
  const [fondOkno, setFondOkno] = useState(false);

  return (
    <div className="space-y-4">
      <div className="bg-brand-50 border border-brand-200 rounded-xl px-4 py-3 flex items-center gap-3">
        <Eye className="w-5 h-5 text-brand-600 shrink-0" />
        <div className="flex-1 text-sm">
          <span className="font-bold text-brand-900 demo-sensitive">Rozhraní: {person.name}</span>
          <span className="text-brand-700"> ({ROLE_LABEL[person.role] || person.role}, read-only náhled, nic za ni/něj nezapíšeš{jeOwner && maFond ? ". Měsíční fond upravíš tlačítkem Upravit fond, měření se tím nemění" : ""})</span>
        </div>
        <button onClick={refreshAll} className="p-1.5 rounded-xl text-brand-600 hover:bg-brand-100 min-h-[44px] sm:min-h-0" title="Obnovit"><RefreshCw className="w-4 h-4" /></button>
        <button onClick={onClose} className="p-1.5 rounded-xl text-brand-600 hover:bg-brand-100 min-h-[44px] sm:min-h-0" title="Zavřít náhled"><X className="w-5 h-5" /></button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between [&>*]:min-w-0 gap-3">
        <p className="text-sm text-slate-500 min-w-0 [overflow-wrap:anywhere]">
          {tab === "kalendar" ? "Čas v kalendáři: klikni na den a uvidíš jeho bloky i denní report."
            : tab === "statistika" ? "Kam čas reálně teče: po činnostech, kvadrantech a úkolech."
            : "Stopky, dnešní záznamy a denní report tak, jak je vidí tento člověk."}
        </p>
        <TimeTabsSwitch tab={tab} onTab={setTab} className="self-start sm:self-auto" />
      </div>

      {tab === "dnes" && (
        <>
          {err && <div className="bg-rose-50 border border-rose-200 text-rose-800 text-sm rounded-xl p-3">{err}</div>}
          {!data && !err && <div className="text-sm text-slate-400 py-6 text-center">Načítám…</div>}
          {data && <TrackerBody data={data} readOnly onAction={async () => {}}
            onUpravitFond={jeOwner && maFond && data.mesicniFond ? () => setFondOkno(true) : null} />}
        </>
      )}
      {/* Kalendář i statistika je jedna komponenta na jednom místě v JSX,
          takže přepnutí záložky nezahodí vybrané období (stejně jako u self view). */}
      {tab !== "dnes" && <TimeKalendar mode={tab} asEmail={person.email} readOnly />}
      {jeOwner && maFond && (
        <FondUpravaModal open={fondOkno} person={person}
          dnesniMesic={String(data?.today || new Date().toISOString().slice(0, 10)).slice(0, 7)}
          onClose={() => setFondOkno(false)} onChanged={load} />
      )}
    </div>
  );
}

export default function OperationsTimeTracking() {
  // Období: měsíc (default, listování zpět jen po měsících s daty) / týden (Po-Ne).
  const [mode, setMode] = useState("mesic");
  const [mesic, setMesic] = useState("");   // "" = aktuální měsíc
  const [tyden, setTyden] = useState("");   // "" = aktuální týden
  const query = mode === "tyden"
    ? `/api/time-report?overview=1&tyden=${tyden || mondayOf(todayIso())}`
    : `/api/time-report?overview=1${mesic ? `&mesic=${mesic}` : ""}`;
  const { data: ov, loading, error, refetch, refreshing } = useApiData(query);
  // Živý pocit: obnovuj přehled každých 20 s (jen když je záložka aktivní), ať se
  // zelené kolečko "právě měří" objeví/zmizí brzy po startu/stopu stopek.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  useEffect(() => {
    const id = setInterval(() => {
      if (typeof document === "undefined" || document.visibilityState === "visible") refetchRef.current?.();
    }, 20000);
    return () => clearInterval(id);
  }, []);
  const [viewAs, setViewAs] = useState(null);
  // 3.9.2026: owner může někomu zapsat časový blok (společná akce s klienty apod.).
  const [blokOtevren, setBlokOtevren] = useState(false);
  const [upravovanyBlok, setUpravovanyBlok] = useState(null); // záznam, který owner upravuje
  // Vyhledávání — připraveno na růst týmu (víc lidí = rychlý filtr dle jména/role).
  const [search, setSearch] = useState("");

  // Řazení dle rolí (17.7.2026 dle ownera): CSM → Manager → Sales → Nutritionist.
  const PORADI_ROLI = { csm: 1, manager: 2, sales: 3, nutritionist: 4, nutricni: 4 };
  const allRows = useMemo(() => {
    const list = [...(ov?.rows || [])];
    list.sort((a, b) =>
      (PORADI_ROLI[String(a.role || "").toLowerCase()] || 9) - (PORADI_ROLI[String(b.role || "").toLowerCase()] || 9)
      || String(a.name || "").localeCompare(String(b.name || ""), "cs"));
    return list;
  }, [ov]); // eslint-disable-line react-hooks/exhaustive-deps
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allRows;
    return allRows.filter((r) => `${r.name} ${r.role}`.toLowerCase().includes(q));
  }, [allRows, search]);

  const kpis = useMemo(() => {
    let totalH = 0, missing = 0, liveH = 0, allH = 0, activeToday = 0;
    let hasToday = false;
    for (const r of allRows) {
      totalH += r.totalHours || 0;
      missing += r.days.filter((d) => d.missing).length;
      liveH += r.origins?.live || 0;
      allH += (r.origins?.live || 0) + (r.origins?.zpetny_start || 0) + (r.origins?.rucni || 0);
      const todayCell = r.days.find((d) => d.today);
      if (todayCell) hasToday = true;
      if ((todayCell?.hours || 0) > 0) activeToday++;
    }
    return {
      totalH: Math.round(totalH * 10) / 10,
      missing,
      livePct: allH > 0 ? Math.round((liveH / allH) * 100) : null,
      activeToday: hasToday ? activeToday : null,
    };
  }, [allRows]);

  // Navigace obdobím
  const period = ov?.period || { mode: "mesic", mesic: todayIso().slice(0, 7) };
  const availableMonths = ov?.availableMonths || [todayIso().slice(0, 7)];
  const curMonday = mondayOf(todayIso());

  const periodLabel = useMemo(() => {
    if (period.mode === "tyden" && period.tyden) {
      const from = period.tyden, to = addDaysIso(period.tyden, 6);
      return `${fmtDayCz(from)} – ${fmtDayCz(to)} ${to.slice(0, 4)}`;
    }
    const m = period.mesic || todayIso().slice(0, 7);
    return `${MONTH_NAMES[Number(m.slice(5, 7)) - 1]} ${m.slice(0, 4)}`;
  }, [period]);

  const nav = useMemo(() => {
    if (mode === "tyden") {
      const mon = period.tyden || curMonday;
      const minMonday = mondayOf(`${availableMonths[0]}-01`);
      // Dopředu jen tam, kde data opravdu jsou (budoucí blok od ownera).
      const maxMonday = mondayOf(ov?.maxDatum || todayIso());
      return {
        prevOk: mon > minMonday,
        nextOk: mon < maxMonday,
        prev: () => setTyden(addDaysIso(mon, -7)),
        next: () => setTyden(addDaysIso(mon, 7)),
      };
    }
    const m = period.mesic || availableMonths[availableMonths.length - 1];
    const idx = availableMonths.indexOf(m);
    return {
      prevOk: idx > 0,
      nextOk: idx >= 0 && idx < availableMonths.length - 1,
      prev: () => setMesic(availableMonths[Math.max(0, idx - 1)]),
      next: () => setMesic(availableMonths[Math.min(availableMonths.length - 1, idx + 1)]),
    };
  }, [mode, period, availableMonths, curMonday, ov]);
  // Návrat na aktuální období. Tlačítko se kreslí, jen když je kam se vracet.
  const mesicDnes = todayIso().slice(0, 7);
  const jeMimoDnesek = mode === "tyden"
    ? (period.tyden || curMonday) !== curMonday
    : (period.mesic || mesicDnes) !== mesicDnes && availableMonths.includes(mesicDnes);
  const naDnesek = () => { if (mode === "tyden") setTyden(curMonday); else setMesic(mesicDnes); };

  if (viewAs) {
    return (
      <Stranka>
        <ViewAsPanel person={viewAs} onClose={() => { setViewAs(null); refetch(); }} />
      </Stranka>
    );
  }

  return (
    <Stranka>
      {/* 4.9.2026 (fáze 4a): hlavička je gradientová jako všude jinde, akce jsou
          jen ikony. Hledání se přesunulo do pruhu filtrů pod hlavičku, protože
          se na telefonu do řádku s tlačítky nevešlo. */}
      <GradientHeader
        title="Time tracking"
        icon={Clock}
        loading={refreshing}
        akce={[
          { klic: "blok", ikona: CalendarPlus, popis: "Přidat blok", onClick: () => setBlokOtevren(true) },
          { klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: refetch },
        ]}
      />

      {(blokOtevren || upravovanyBlok) && (
        <PridatBlokModal
          lide={allRows}
          blok={upravovanyBlok}
          onClose={() => { setBlokOtevren(false); setUpravovanyBlok(null); }}
          onSaved={() => { setBlokOtevren(false); setUpravovanyBlok(null); refetch(); }}
        />
      )}

      {error && (
        <Hlaska ton="problem" title="Time tracking se nepodařilo načíst" akce={<AkceZnovu onClick={refetch} />}>
          {String(error.message || error)}
        </Hlaska>
      )}

      {/* Období a hledání v jednom pruhu. Měsíc/Týden je samostatný segmentový
          přepínač nad obdobím (schváleno 4. 9. 2026, varianta A) — je to stejná
          komponenta jako Aktivní/Archiv u notifikací, takže nepřidává nový vzor.
          Šipka se nezobrazí, když tím směrem není kam jít (žádné zašedlé pahýly). */}
      <PruhFiltru>
        <Segment
          aria-label="Měsíc nebo týden"
          value={mode}
          onChange={(k) => { if (k === "tyden") { setMode("tyden"); if (!tyden) setTyden(curMonday); } else setMode("mesic"); }}
          items={[{ key: "mesic", label: "Měsíc" }, { key: "tyden", label: "Týden" }]}
        />
        <div className="flex items-stretch gap-2">
          {nav.prevOk ? (
            <button type="button" className="btn-mesic" onClick={nav.prev} aria-label="Předchozí období" title="Předchozí období">
              <ChevronLeft className="h-5 w-5 sm:h-4 sm:w-4" />
            </button>
          ) : <span className="w-11 shrink-0 sm:w-10" aria-hidden="true" />}
          <div className="flex min-h-[44px] flex-1 items-center justify-center whitespace-nowrap rounded-xl border border-brand/20 bg-white px-3 text-base font-bold tabular-nums text-slate-900 sm:min-h-0 sm:py-1.5 sm:text-sm">
            {periodLabel}
          </div>
          {nav.nextOk ? (
            <button type="button" className="btn-mesic" onClick={nav.next} aria-label="Následující období" title="Následující období">
              <ChevronRight className="h-5 w-5 sm:h-4 sm:w-4" />
            </button>
          ) : <span className="w-11 shrink-0 sm:w-10" aria-hidden="true" />}
        </div>
        <div className="flex items-stretch gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Hledat člověka…"
            className="field min-w-0 flex-1"
          />
          {jeMimoDnesek && (
            <button type="button" className="btn btn-secondary shrink-0" onClick={naDnesek}>Dnes</button>
          )}
        </div>
      </PruhFiltru>

      {/* Čísla nahoře. Barevný proužek dlaždice nese význam. */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Dlazdice popisek="Hodin za období" hodnota={ov ? `${kpis.totalH} h` : "—"} ton="znacka"
          doplnky={<Stitek velikost="mala" ton="neutralni">celý tým</Stitek>} />
        <Dlazdice popisek="Dnes aktivní" hodnota={ov && kpis.activeToday != null ? kpis.activeToday : "—"} ton="ok"
          doplnky={<Stitek velikost="mala" ton="neutralni">{kpis.activeToday != null ? `z ${allRows.length} lidí` : "mimo aktuální období"}</Stitek>} />
        <Dlazdice popisek="Chybějící reporty" hodnota={ov ? kpis.missing : "—"} ton="problem"
          hodnotaTon={ov && kpis.missing > 0 ? "problem" : undefined}
          doplnky={<Stitek velikost="mala" ton="neutralni">pracovní dny bez odrážek</Stitek>} />
        <Dlazdice popisek="Měřeno živě" hodnota={ov && kpis.livePct != null ? `${kpis.livePct} %` : "—"} ton="pozor"
          doplnky={<Stitek velikost="mala" ton="neutralni">zbytek vloženo zpětně</Stitek>} />
      </div>

      {/* Karty lidí — jeden člověk na řádek, velký kalendář */}
      {loading && !ov ? (
        <div className="glass !p-0"><SkeletonRows rows={4} /></div>
      ) : rows.length === 0 ? (
        <div className="glass">
          <EmptyState
            icon={Users}
            title={search.trim() ? "Nikdo neodpovídá hledání" : "Žádní lidé s time trackingem"}
            hint={search.trim() ? "Zkus jiné jméno nebo roli." : "Role manager, csm, sales a nutritionist se nastavují v Nastavení > Uživatelé."}
          />
        </div>
      ) : (
        <div className="space-y-5">
          {rows.map((r) => <PersonCard key={r.email} row={r} onViewAs={setViewAs} onChanged={refetch}
            onUpravitBlok={(e) => setUpravovanyBlok(e)} />)}
        </div>
      )}

      {/* Správa záznamů: archivace měření (jen owner tuto stránku vidí) */}
      <EntriesManager onChanged={refetch} onUpravitBlok={(e) => setUpravovanyBlok(e)} />
    </Stranka>
  );
}


// === Owner zapíše časový blok jinému člověku (3.9.2026) ===
// Typicky společná akce s klienty: owner ji zapíše celému týmu, nikdo si to nemusí
// hlídat sám. Blok je v přehledu označený jako zadaný ownerem, dotyčný dostane
// zprávu na Slack a po tu dobu si nemůže spustit vlastní měření.
function PridatBlokModal({ lide, blok = null, onClose, onSaved }) {
  const dnes = todayIso();
  const jeUprava = !!blok;
  const [email, setEmail] = useState(blok?.email || lide[0]?.email || "");
  const [datum, setDatum] = useState(blok?.datum || dnes);
  const [od, setOd] = useState(blok?.od || "09:00");
  const [doC, setDoC] = useState(blok?.do || "12:00");
  const [oblast, setOblast] = useState(blok?.oblast || "");
  const [popis, setPopis] = useState(blok?.popis || "");
  const [busy, setBusy] = useState(false);
  const [chyba, setChyba] = useState("");

  const clovek = lide.find((l) => l.email === email) || null;
  const cinnosti = clovek?.cinnosti || clovek?.activities || [];

  useEffect(() => {
    if (cinnosti.length && !cinnosti.some((c) => (c.key || c) === oblast)) {
      setOblast(cinnosti[0].key || cinnosti[0]);
    }
  }, [email]); // eslint-disable-line react-hooks/exhaustive-deps

  const delka = (() => {
    const [h1, m1] = od.split(":").map(Number);
    const [h2, m2] = doC.split(":").map(Number);
    const min = (h2 * 60 + m2) - (h1 * 60 + m1);
    return Number.isFinite(min) ? min : 0;
  })();

  async function uloz() {
    setBusy(true); setChyba("");
    try {
      const r = await apiFetch("/api/time-report", {
        method: "POST",
        body: JSON.stringify(jeUprava
          ? { action: "ownerBlokUprava", id: blok.id, datum, od, do: doC, oblast, popis: popis.trim() }
          : { action: "ownerBlok", email, datum, od, do: doC, oblast, popis: popis.trim() }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      onSaved?.();
    } catch (e) { setChyba(e.message); }
    finally { setBusy(false); }
  }

  // 4.9.2026 (fáze 4a): okno ze vzorníku — na telefonu vyjede zespodu, patka se
  // odlepí od home indikátoru a při psaní se opře o klávesnici. Pole jsou .field
  // a .label. Chování a data zůstávají stejná.
  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title={jeUprava ? "Upravit časový blok" : "Přidat časový blok"}
      footer={(
        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="btn btn-secondary flex-1">Zrušit</button>
          <button type="button" onClick={uloz} disabled={busy || delka <= 0 || !oblast || !email} className="btn btn-primary flex-1">
            {busy ? "Ukládám…" : jeUprava ? "Uložit změnu" : "Zapsat blok"}
          </button>
        </div>
      )}
    >
      <div className="space-y-3">
        <p className="text-xs text-slate-500">
          {jeUprava ? "Změnu dostane dotyčný na Slack." : "Zapíše se za člověka, dostane o tom zprávu na Slack."}
        </p>

        <label className="block">
          <span className="label">Komu</span>
          <select value={email} onChange={(e) => setEmail(e.target.value)} disabled={jeUprava} className="field">
            {lide.map((l) => <option key={l.email} value={l.email}>{l.name} ({ROLE_LABEL[String(l.role || "").toLowerCase()] || l.role})</option>)}
          </select>
        </label>

        <div className="grid grid-cols-3 gap-2">
          <label className="block">
            <span className="label">Datum</span>
            {/* 3.9.2026 (přání Ládi): blok jde zapsat i dopředu, například na
                akci, která teprve bude. Datum proto není omezené na dnešek. */}
            <input type="date" value={datum} onChange={(e) => setDatum(e.target.value)} className="field" />
          </label>
          <label className="block">
            <span className="label">Od</span>
            <input type="time" value={od} onChange={(e) => setOd(e.target.value)} className="field" />
          </label>
          <label className="block">
            <span className="label">Do</span>
            <input type="time" value={doC} onChange={(e) => setDoC(e.target.value)} className="field" />
          </label>
        </div>

        <label className="block">
          <span className="label">Činnost</span>
          <select value={oblast} onChange={(e) => setOblast(e.target.value)} className="field">
            {cinnosti.length === 0 && <option value="">Načítám činnosti…</option>}
            {cinnosti.map((c) => {
              const k = c.key || c;
              return <option key={k} value={k}>{c.label || k}</option>;
            })}
          </select>
        </label>

        <label className="block">
          <span className="label">Poznámka</span>
          <input type="text" value={popis} onChange={(e) => setPopis(e.target.value)}
            placeholder="např. Aktivní víkend s klientkami" className="field" />
        </label>

        <div className={`text-sm font-semibold ${delka > 0 ? "text-slate-600" : "text-rose-600"}`}>
          {delka > 0
            ? `Délka bloku: ${Math.round((delka / 60) * 10) / 10} h${datum > dnes ? " (dopředu, na plánovanou akci)" : ""}`
            : "Konec musí být později než začátek"}
        </div>
        {chyba && <Hlaska ton="problem" title="Uložení selhalo">{chyba}</Hlaska>}
      </div>
    </Modal>
  );
}
